import { ColDef, ColGroupDef, CellStyle } from "ag-grid-community";
import {
  MatchedRecord,
  GhostDeclinedRecord,
  GhostApprovedRecord,
  OrphanMXRecord,
  OrphanRoninRecord,
  IdMismatchedRecord,
  VoidedOrderRecord,
  DuplicateMXRecord,
  UnmatchedRefRecord,
  StatusMismatchRecord,
  ReconciliationResponse,
  MXCsvRecord,
  CsvAnalysis,
  FixItRow,
  DoubleChargeRecord,
} from "./types";

export function formatCents(cents: number | null | undefined): string {
  if (cents == null) return "";
  return `$${(cents / 100).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Sending big uploads in pieces (RP-2535)
// ---------------------------------------------------------------------------
// Vercel caps every API request AND response at 4.5 MB. Gzipping one big
// request was enough until Fall for Greenville: 190k Stripe rows were ~7.8 MB
// gzipped going up, and the answer coming back was ~100 MB of JSON. So rows go
// up in pieces to /api/mx-recon-staging, the endpoint is called with just the
// upload id, and a big answer comes back in pieces the same way. The server
// side lives in ronin-api services/reconStaging.js.

/** Rows per uploaded piece: ~1 MB gzipped for a Stripe export. */
const STAGE_ROWS_PER_PART = 20000;
/** A piece bigger than this (after gzip) is split in half again. */
const STAGE_MAX_PART_BYTES = 3_500_000;
/** Pieces sent or fetched at the same time. */
const STAGE_CONCURRENCY = 4;

async function gzipJson(value: unknown): Promise<Blob> {
  return new Response(
    new Blob([JSON.stringify(value)]).stream().pipeThrough(new CompressionStream("gzip"))
  ).blob();
}

/** Gzip rows into pieces that each stay under STAGE_MAX_PART_BYTES. */
async function gzipRowsInParts(rows: unknown[]): Promise<Blob[]> {
  const parts: Blob[] = [];
  const pack = async (slice: unknown[]): Promise<void> => {
    const gz = await gzipJson(slice);
    if (gz.size > STAGE_MAX_PART_BYTES && slice.length > 1) {
      const mid = Math.ceil(slice.length / 2);
      await pack(slice.slice(0, mid));
      await pack(slice.slice(mid));
    } else {
      parts.push(gz);
    }
  };
  if (rows.length === 0) return [await gzipJson([])];
  for (let i = 0; i < rows.length; i += STAGE_ROWS_PER_PART) {
    await pack(rows.slice(i, i + STAGE_ROWS_PER_PART));
  }
  return parts;
}

/** Run fn(0..count-1) with at most `limit` running at once; results in order. */
async function runLimited<T>(count: number, limit: number, fn: (i: number) => Promise<T>): Promise<T[]> {
  const results = new Array<T>(count);
  let next = 0;
  const worker = async () => {
    while (next < count) {
      const i = next++;
      results[i] = await fn(i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, count) }, worker));
  return results;
}

async function errorFrom(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => ({}));
  return new Error(body.error || `${fallback} ${res.status}`);
}

/**
 * POST `rows` (plus `body`) to a ronin-api endpoint without hitting the 4.5 MB cap.
 *
 * Rows are uploaded in gzipped pieces, then the endpoint is called with
 * { ...body, uploadId, inputParts }. If it answers { resultParts }, the result
 * is downloaded in pieces and unzipped; otherwise its answer is returned as is.
 * The upload is deleted on the server afterwards either way.
 */
export async function postStaged<T>(
  apiBase: string,
  path: string,
  rows: unknown[],
  body: Record<string, unknown>,
  token: string | null
): Promise<T> {
  const auth: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const stagingUrl = `${apiBase}/api/mx-recon-staging`;
  const uploadId = crypto.randomUUID();

  const sendPart = async (part: Blob, i: number) => {
    const res = await fetch(`${stagingUrl}?uploadId=${uploadId}&part=${i}`, {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream", ...auth },
      body: part,
    });
    if (!res.ok) throw await errorFrom(res, `Upload of part ${i + 1} failed:`);
  };

  try {
    const parts = await gzipRowsInParts(rows);
    // Part 0 goes first on its own: the server clears out stale uploads on it.
    await sendPart(parts[0], 0);
    await runLimited(parts.length - 1, STAGE_CONCURRENCY, (i) => sendPart(parts[i + 1], i + 1));

    const res = await fetch(`${apiBase}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth },
      body: JSON.stringify({ ...body, uploadId, inputParts: parts.length }),
    });
    if (!res.ok) throw await errorFrom(res, "Server error");
    const reply = await res.json();
    if (typeof reply?.resultParts !== "number") return reply as T;

    const pieces = await runLimited(reply.resultParts, STAGE_CONCURRENCY, async (i) => {
      const r = await fetch(`${stagingUrl}?uploadId=${uploadId}&part=${i}`, { headers: auth, cache: "no-store" });
      if (!r.ok) throw await errorFrom(r, `Download of result part ${i + 1} failed:`);
      return r.blob();
    });
    const text = await new Response(
      new Blob(pieces).stream().pipeThrough(new DecompressionStream("gzip"))
    ).text();
    return JSON.parse(text) as T;
  } finally {
    // Best effort; the server also sweeps uploads older than 6 hours.
    fetch(`${stagingUrl}?uploadId=${uploadId}`, { method: "DELETE", headers: auth }).catch(() => undefined);
  }
}

export function formatDate(d: string | null | undefined): string {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
}

export const MX_CSV_COLUMN_MAP: Record<string, string> = {
  // Date columns
  date: "date",
  "date/time": "date",
  "transaction date": "date",
  "trans date": "date",
  // Type columns
  type: "type",
  "tran type": "type",
  "transaction type": "type",
  "trans type": "type",
  // Status
  status: "status",
  "transaction status": "status",
  // Amount
  amount: "amount",
  total: "amount",
  "total amount": "amount",
  // Invoice / Reference
  invoice: "invoice",
  "invoice #": "invoice",
  "invoice number": "invoice",
  // Memo
  memo: "memo",
  notes: "memo",
  description: "memo",
  // Card info
  last4: "last4",
  "last 4": "last4",
  "card last 4": "last4",
  "card number": "last4",
  tender: "last4",
  // Card type
  cardtype: "cardType",
  "card type": "cardType",
  "card brand": "cardType",
  // Reference (MX Reference ID)
  reference: "mxReference",
};

export function mapCsvHeaders(rawHeaders: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (const raw of rawHeaders) {
    const key = raw.trim().toLowerCase();
    if (MX_CSV_COLUMN_MAP[key]) {
      mapping[raw] = MX_CSV_COLUMN_MAP[key];
    }
  }
  return mapping;
}

/* ------------------------------------------------------------------ */
/*  Processor detection & Stripe support                              */
/* ------------------------------------------------------------------ */

export type ProcessorKind = "mx" | "stripe";

/**
 * Detect which payment processor a CSV export came from by its headers.
 *
 * Stripe "unified payments" exports carry unmistakable columns like
 * `PaymentIntent ID` and `Created date (UTC)`; MX Merchant exports use
 * `Invoice` / `Memo` / `Date/Time`. Anything that doesn't look like Stripe
 * falls through to the MX path, which then enforces its own required
 * columns — so unrecognized files still produce a useful error message.
 */
export function detectProcessor(rawHeaders: string[]): ProcessorKind {
  const headers = new Set(rawHeaders.map((h) => h.trim().toLowerCase()));
  if (
    headers.has("paymentintent id") ||
    (headers.has("created date (utc)") && headers.has("amount refunded"))
  ) {
    return "stripe";
  }
  return "mx";
}

/**
 * Human-facing labels for the processor(s) behind the current reconciliation.
 *  - `short` is used as a column-header prefix ("Stripe Status", "MX Status").
 *  - `name` is used in prose and titles ("Stripe shows the refund…").
 * Mixed or unknown uploads fall back to generic processor wording.
 */
export function getProcessorLabels(processorNames: string[]): { short: string; name: string } {
  const unique = [...new Set(processorNames)];
  if (unique.length === 1) {
    if (unique[0] === "MX Merchant") return { short: "MX", name: "MX" };
    return { short: unique[0], name: unique[0] };
  }
  return { short: "Proc.", name: "Processor" };
}

/**
 * Re-label ag-grid column headers for the active processor: any headerName
 * starting with "MX " or "Proc. " gets the given prefix instead
 * (e.g. "MX Status" / "Proc. Status" → "Stripe Status"). Recurses into
 * column groups. Returns new defs; the static arrays stay untouched.
 */
export function applyProcessorLabel<T extends ColDef<any> | ColGroupDef<any>>(
  cols: T[],
  shortLabel: string
): T[] {
  const relabel = (h: string) =>
    h.replace(/^MX /, `${shortLabel} `).replace(/^Proc\. /, `${shortLabel} `);
  return cols.map((c) => {
    const next: any = { ...c };
    if (typeof next.headerName === "string") next.headerName = relabel(next.headerName);
    if (Array.isArray(next.children)) next.children = applyProcessorLabel(next.children, shortLabel);
    return next as T;
  });
}

/** Lower-cased Stripe unified-payments headers we read. */
const STRIPE_COLUMNS = {
  chargeId: "id",
  created: "created date (utc)",
  amount: "amount",
  amountRefunded: "amount refunded",
  refundedDate: "refunded date (utc)",
  status: "status",
  declineReason: "decline reason",
  sellerMessage: "seller message",
  paymentIntent: "paymentintent id",
  cardBrand: "card brand",
  cardLast4: "card last4",
  referenceId: "reference_id (metadata)",
} as const;

export const STRIPE_REQUIRED_COLUMNS = ["Status", "Amount", "reference_id (metadata)"];

/**
 * Stripe "YYYY-MM-DD HH:mm:ss" timestamps are UTC but carry no zone marker —
 * `new Date()` would read them as local time. Re-shape to ISO-8601 UTC.
 */
function stripeDateToIso(d: string): string {
  const t = (d || "").trim();
  if (!t) return "";
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(t)) return t.replace(" ", "T") + "Z";
  return t;
}

/**
 * Translate a Stripe charge Status onto the MX status vocabulary the server
 * and the status-bucket logic already understand.
 */
function stripeStatusToMx(status: string, declineReason: string): string {
  const s = status.trim().toLowerCase();
  // "Refunded" means the charge itself succeeded; the refund is emitted as a
  // separate synthetic Return row by mapStripeRecords.
  if (s === "paid" || s === "refunded") return "Approved";
  if (s === "failed") return declineReason ? `Declined (${declineReason})` : "Declined";
  // Abandoned/incomplete checkout attempts — no charge was ever created.
  // Kept as declined so a Ronin order wrongly marked paid against one of
  // these still surfaces in Ghost Declined.
  if (s === "requires_payment_method") return "Declined (incomplete)";
  if (s === "canceled" || s === "cancelled") return "Declined (canceled)";
  return status; // pass through anything unexpected
}

/**
 * Normalize rows parsed from a Stripe unified-payments CSV into the same
 * MXCsvRecord shape the MX path produces, so the rest of the pipeline
 * (event analysis, reconciliation API, grids) works unchanged:
 *
 *  - `reference_id (metadata)` holds the clean Ronin reference_id (no Excel
 *    mangling, no memo digging). It goes into `invoice`, and into a memo
 *    JSON blob, so parseReferenceId() resolves it via either path.
 *  - Statuses are translated to MX vocabulary (Paid → Approved, …).
 *  - Refunds: Stripe stores `Amount Refunded` on the original charge row,
 *    while MX represents refunds as separate negative "Return" rows. We emit
 *    a synthetic Return row per refunded charge so totals stay comparable.
 *  - The memo also carries the Stripe charge id / payment intent / seller
 *    message, which shows up in the Orphans grid Memo column.
 */
export function mapStripeRecords(
  rows: Record<string, string>[],
  rawHeaders: string[]
): MXCsvRecord[] {
  // Resolve the actual header spellings case-insensitively.
  const col: Partial<Record<keyof typeof STRIPE_COLUMNS, string>> = {};
  for (const raw of rawHeaders) {
    const key = raw.trim().toLowerCase();
    for (const [field, name] of Object.entries(STRIPE_COLUMNS)) {
      if (key === name) col[field as keyof typeof STRIPE_COLUMNS] = raw;
    }
  }
  const get = (row: Record<string, string>, field: keyof typeof STRIPE_COLUMNS): string => {
    const h = col[field];
    return h ? (row[h] || "").trim() : "";
  };

  const records: MXCsvRecord[] = [];
  for (const row of rows) {
    const ref = get(row, "referenceId");
    const declineReason = get(row, "declineReason");
    const date = stripeDateToIso(get(row, "created"));
    const last4 = get(row, "cardLast4");
    const cardType = get(row, "cardBrand");

    const memoPayload: Record<string, string> = {};
    if (ref) memoPayload.reference_id = ref;
    const chargeId = get(row, "chargeId");
    if (chargeId) memoPayload.stripe_charge_id = chargeId;
    const intent = get(row, "paymentIntent");
    if (intent) memoPayload.stripe_payment_intent = intent;
    const message = get(row, "sellerMessage") || declineReason;
    if (message) memoPayload.stripe_message = message;
    const memo = JSON.stringify(memoPayload);

    records.push({
      date,
      type: "Sale",
      status: stripeStatusToMx(get(row, "status"), declineReason),
      amount: get(row, "amount"),
      invoice: ref,
      memo,
      last4,
      cardType,
      paymentIntentId: intent,
    });

    // Synthetic Return row for any refunded portion (full or partial).
    // Accounting parens make the amount negative, matching MX's convention.
    const refunded = parseFloat(get(row, "amountRefunded") || "0");
    if (!isNaN(refunded) && refunded > 0) {
      records.push({
        date: stripeDateToIso(get(row, "refundedDate")) || date,
        type: "Return",
        status: "Approved",
        amount: `(${refunded.toFixed(2)})`,
        invoice: ref,
        memo,
        last4,
        cardType,
        paymentIntentId: intent,
      });
    }
  }

  // Sequential _rowIndex across emitted rows (synthetics included) — used to
  // stitch memos back onto orphan rows after the server round-trip.
  records.forEach((r, i) => { r._rowIndex = i; });
  return records;
}

/**
 * Parse reference_id from MX invoice/memo fields (mirrors server-side logic).
 *
 * The Invoice column is unreliable: MX's CSV export runs through an
 * Excel-flavoured engine that mangles cells starting with '-', '=', '+', or
 * '@' (interprets them as formulas) and overwrites them with Excel error
 * strings like #NAME?. The Memo column is JSON-quoted and immune to that
 * corruption, so we prefer it whenever it yields a reference_id.
 */
const EXCEL_ERROR_INVOICES = new Set([
  "#NAME?", "#REF!", "#VALUE!", "#NULL!", "#DIV/0!",
  "#N/A", "#NUM!", "#ERROR!", "#GETTING_DATA",
]);

// Excel auto-converts long numeric strings to scientific notation on save
// (e.g. "612402000000" → "6.12402E+11"). The conversion loses precision and
// collapses many distinct refs onto the same string, so we treat any such
// value as unparseable and let the server's amount + last4 ID-mismatch
// recovery pair each row to the correct Ronin order. Mirrors the server-side
// EXCEL_SCI_NOTATION in api/mx-merchant-reconciliation.js.
const EXCEL_SCI_NOTATION = /^[+-]?\d+(\.\d+)?[Ee][+-]?\d+$/;

function readReferenceIdFromMemo(memo: string): string | null {
  try {
    const parsed = JSON.parse(memo);
    if (parsed?.data?.localOrder?.reference_id) {
      return parsed.data.localOrder.reference_id;
    }
    if (parsed?.order?.reference_id) {
      return parsed.order.reference_id;
    }
  } catch {
    // Not valid JSON — fall through to regex.
  }
  const match = memo.match(/"reference_id"\s*:\s*"([^"]+)"/);
  return match ? match[1] : null;
}

export function parseReferenceId(invoice?: string, memo?: string): string | null {
  // Always try memo first — it's immune to Excel mangling.
  if (memo) {
    const fromMemo = readReferenceIdFromMemo(memo);
    if (fromMemo) return fromMemo;
  }

  // Fall back to invoice if it looks like a real reference_id:
  //   - not an Excel error sentinel (#NAME?, #REF!, ...)
  //   - not a gift-card-style 4000000 prefix
  //   - not Excel-mangled scientific notation (e.g. "6.12402E+11")
  if (invoice) {
    const trimmed = invoice.trim();
    if (
      trimmed &&
      !EXCEL_ERROR_INVOICES.has(trimmed) &&
      !trimmed.startsWith("4000000") &&
      !EXCEL_SCI_NOTATION.test(trimmed)
    ) {
      return trimmed;
    }
  }

  return null;
}

/** Analyze parsed CSV records to extract date range, status breakdown, and totals */
export function analyzeCsv(records: MXCsvRecord[]): CsvAnalysis {
  const dates: Date[] = [];
  const statusBreakdown: Record<string, number> = {};
  let totalAmount = 0;

  for (const r of records) {
    // Parse dates
    if (r.date) {
      try {
        const d = new Date(r.date);
        if (!isNaN(d.getTime())) dates.push(d);
      } catch { /* skip bad dates */ }
    }

    // Count statuses
    const status = (r.status || "Unknown").trim();
    statusBreakdown[status] = (statusBreakdown[status] || 0) + 1;

    // Sum amounts — accounting parens ($19.23) parse as -19.23 so refunds
    // naturally subtract from the gross total (matches MX's CSV convention).
    if (r.amount) {
      let cleaned = r.amount.replace(/[$,\s]/g, "");
      let negative = false;
      if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
        cleaned = cleaned.slice(1, -1);
        negative = true;
      }
      const num = parseFloat(cleaned);
      if (!isNaN(num)) totalAmount += negative ? -num : num;
    }
  }

  dates.sort((a, b) => a.getTime() - b.getTime());

  return {
    totalRows: records.length,
    dateRange: dates.length > 0
      ? {
          earliest: dates[0].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
          latest: dates[dates.length - 1].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
        }
      : null,
    statusBreakdown,
    totalAmount,
  };
}

/* ------------------------------------------------------------------ */
/*  Fix-It export — one flat, ops-reviewable remediation list          */
/* ------------------------------------------------------------------ */

/**
 * Walk every actionable reconciliation category and emit one row per item that
 * needs to be adjusted / deleted / added, with the recommended action. Ops can
 * review the export, fill the `ops_decision` column, and hand the list back.
 */
export function compileFixItRows(data: ReconciliationResponse): FixItRow[] {
  const rows: FixItRow[] = [];
  const money = (cents?: number | null) => (cents == null ? "" : formatCents(cents));

  // Duplicates (dup charge) → VOID the dup, keep the order that owns the charge.
  for (const m of data.idMismatched ?? []) {
    if (m.matched_by !== "PaymentIntent (dup charge)") continue;
    rows.push({
      action: "VOID", category: "Duplicate order",
      ronin_order_id: String(m.ronin_order_id), reference_id: m.ronin_reference_id,
      keeper_order_id: m.keeper_order_id != null ? String(m.keeper_order_id) : "",
      stripe_reference_id: m.mx_reference_id, payment_intent_id: m.payment_intent_id ?? "",
      ronin_amount: money(m.ronin_amount_cents), stripe_amount: money(m.mx_amount_cents),
      location: m.location_name, date: m.ronin_transaction_at,
      recommended_fix: "Duplicate order — the charge belongs to the keeper order. Void this one.",
      ops_decision: "",
    });
  }

  // Double charges → REFUND the duplicate PaymentIntent(s).
  for (const dc of data.doubleCharges ?? []) {
    rows.push({
      action: "REFUND", category: "Double charge",
      ronin_order_id: dc.ronin_order_id != null ? String(dc.ronin_order_id) : "",
      reference_id: dc.reference_id, keeper_order_id: "", stripe_reference_id: dc.reference_id,
      payment_intent_id: dc.refund_payment_intents,
      ronin_amount: money(dc.should_be_cents), stripe_amount: money(dc.total_charged_cents),
      location: dc.location_name, date: dc.mx_date,
      recommended_fix: `Customer charged ${dc.times_charged}× — refund ${money(dc.overcharge_cents)}. Refund PaymentIntent(s): ${dc.refund_payment_intents}. Keep ${dc.keep_payment_intent}.`,
      ops_decision: "",
    });
  }

  // Ghost Approved → Stripe charged, Ronin says declined. Update Ronin payment to approved.
  for (const g of data.ghostApproved ?? []) {
    rows.push({
      action: "UPDATE_STATUS", category: "Ghost approved",
      ronin_order_id: String(g.ronin_order_id), reference_id: g.reference_id,
      keeper_order_id: "", stripe_reference_id: g.reference_id, payment_intent_id: "",
      ronin_amount: money(g.ronin_amount_cents), stripe_amount: money(g.mx_amount_cents),
      location: g.location_name, date: g.ronin_transaction_at,
      recommended_fix: "Stripe confirms the charge but Ronin payment is declined — set Ronin payment status to approved.",
      ops_decision: "",
    });
  }

  // Refund mismatches (matched order=refund, payment still a charge) → update payment to refund.
  for (const m of data.matched ?? []) {
    const os = (m.ronin_status || "").toLowerCase();
    const ps = (m.payment_status || "").toLowerCase();
    if (os === "refund" && ps !== "refund" && ps !== "refunded") {
      rows.push({
        action: "UPDATE_STATUS", category: "Refund mismatch",
        ronin_order_id: String(m.ronin_order_id), reference_id: m.reference_id,
        keeper_order_id: "", stripe_reference_id: m.reference_id, payment_intent_id: "",
        ronin_amount: money(m.ronin_amount_cents), stripe_amount: money(m.mx_amount_cents),
        location: m.location_name, date: m.ronin_transaction_at,
        recommended_fix: "Order is refund but the payment still holds the charge — update payment to refund.",
        ops_decision: "",
      });
    }
  }

  // Ghost Declined → Ronin approved, Stripe declined/incomplete. Verify, then void.
  for (const g of data.ghostDeclined ?? []) {
    rows.push({
      action: "VERIFY/VOID", category: "Ghost declined",
      ronin_order_id: String(g.ronin_order_id), reference_id: g.reference_id,
      keeper_order_id: "", stripe_reference_id: g.reference_id, payment_intent_id: "",
      ronin_amount: money(g.ronin_amount_cents), stripe_amount: money(g.mx_amount_cents),
      location: g.location_name, date: g.ronin_transaction_at,
      recommended_fix: "Ronin approved but Stripe charge is declined/incomplete — confirm the real charge exists elsewhere, then void.",
      ops_decision: "",
    });
  }

  // Orphan Stripe (approved, not in Ronin) → add the order to Ronin.
  for (const o of data.orphanMX ?? []) {
    const s = (o.mx_status || "").toLowerCase();
    if (!s.includes("approved") && !s.includes("settled")) continue;
    rows.push({
      action: "ADD", category: "Orphan Stripe (not in Ronin)",
      ronin_order_id: "", reference_id: o.reference_id,
      keeper_order_id: "", stripe_reference_id: o.reference_id, payment_intent_id: o.payment_intent_id ?? "",
      ronin_amount: "", stripe_amount: money(o.mx_amount_cents),
      location: "", date: o.mx_date,
      recommended_fix: "Approved Stripe charge with no Ronin order — add the order to Ronin (or confirm it belongs to another event).",
      ops_decision: "",
    });
  }

  // Orphan Ronin (not in Stripe) → verify.
  for (const o of data.orphanRonin ?? []) {
    rows.push({
      action: "VERIFY", category: "Orphan Ronin (not in Stripe)",
      ronin_order_id: String(o.ronin_order_id), reference_id: o.reference_id,
      keeper_order_id: "", stripe_reference_id: "", payment_intent_id: o.payment_intent_id ?? "",
      ronin_amount: money(o.ronin_amount_cents), stripe_amount: "",
      location: o.location_name, date: o.ronin_transaction_at,
      recommended_fix: "Ronin order with no Stripe charge in this upload — verify (different Stripe account, unsettled, or genuinely missing).",
      ops_decision: "",
    });
  }

  // Matched amount differences → verify.
  for (const m of data.matched ?? []) {
    if (!m.difference_cents) continue;
    rows.push({
      action: "VERIFY", category: "Amount difference",
      ronin_order_id: String(m.ronin_order_id), reference_id: m.reference_id,
      keeper_order_id: "", stripe_reference_id: m.reference_id, payment_intent_id: "",
      ronin_amount: money(m.ronin_amount_cents), stripe_amount: money(m.mx_amount_cents),
      location: m.location_name, date: m.ronin_transaction_at,
      recommended_fix: `Ronin and Stripe amounts differ by ${money(m.difference_cents)} — verify which is correct.`,
      ops_decision: "",
    });
  }

  return rows;
}

/** Build the Fix-It list and trigger a CSV download. Returns the row count. */
export function downloadFixItCsv(data: ReconciliationResponse, eventName: string): number {
  const rows = compileFixItRows(data);
  const headers: (keyof FixItRow)[] = [
    "action", "category", "ronin_order_id", "reference_id", "keeper_order_id",
    "stripe_reference_id", "payment_intent_id", "ronin_amount", "stripe_amount",
    "location", "date", "recommended_fix", "ops_decision",
  ];
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [
    headers.join(","),
    ...rows.map((r) => headers.map((h) => esc(r[h])).join(",")),
  ].join("\n");

  const slug = (eventName || "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fix-it-list-${slug}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return rows.length;
}

const centsFormatter = (p: { value: number | null }) => formatCents(p.value);
const dateFormatter = (p: { value: string | null }) => formatDate(p.value);

export const matchedColumns: ColDef<MatchedRecord>[] = [
  {
    headerName: "Flag", width: 170, pinned: "left",
    valueGetter: (p) => {
      if (!p.data) return "";
      const flags: string[] = [];
      if (p.data.rescued_outside_date_range) flags.push("OUTSIDE DATE RANGE");
      const orderStatus = (p.data.ronin_status || "").toLowerCase();
      const payStatus = (p.data.payment_status || "").toLowerCase();
      if (orderStatus === "refund" && payStatus !== "refund" && payStatus !== "refunded")
        flags.push("REFUND MISMATCH");
      if (p.data.difference_cents !== 0) flags.push("AMOUNT DIFF");
      return flags.join(", ");
    },
    cellStyle: (p): CellStyle | undefined => {
      const v = p.value as string;
      if (v.includes("REFUND MISMATCH")) return { color: "#fff", backgroundColor: "#dc2626", fontWeight: "bold", fontSize: "11px" };
      if (v.includes("OUTSIDE DATE RANGE")) return { color: "#7c3aed", fontWeight: "bold", fontSize: "11px" };
      if (v.includes("AMOUNT DIFF")) return { color: "#ea580c", fontWeight: "bold", fontSize: "11px" };
      return undefined;
    },
    sort: "desc",
  },
  { field: "reference_id", headerName: "Reference ID", width: 200 },
  { field: "ronin_order_id", headerName: "Ronin Order", width: 120 },
  { field: "location_name", headerName: "Location", width: 160 },
  // Statuses grouped: Ronin side then MX side for easy comparison
  { field: "ronin_status", headerName: "Order Status", width: 110 },
  { field: "payment_status", headerName: "Pay Status", width: 110 },
  { field: "transaction_status", headerName: "Txn Status", width: 110 },
  { field: "mx_status", headerName: "MX Status", width: 180 },
  // Amounts grouped together
  { field: "ronin_amount_cents", headerName: "Ronin Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_amount_cents", headerName: "MX Amount", width: 130, valueFormatter: centsFormatter },
  {
    field: "difference_cents", headerName: "Difference", width: 120, valueFormatter: centsFormatter,
    cellStyle: (p): CellStyle | undefined => {
      if (p.value !== 0) return { color: "#dc2626", fontWeight: "bold" };
      return undefined;
    },
  },
  { field: "mx_date", headerName: "MX Date", width: 160, valueFormatter: dateFormatter },
  { field: "ronin_transaction_at", headerName: "Ronin Date", width: 160, valueFormatter: dateFormatter },
  { field: "mx_last4", headerName: "MX Last 4", width: 100 },
  { field: "ronin_last4", headerName: "Ronin Last 4", width: 100 },
  { field: "mx_card_type", headerName: "MX Card Type", width: 120 },
  { field: "ronin_card_type", headerName: "Ronin Card Type", width: 120 },
  { field: "payment_intent_id", headerName: "Stripe PaymentIntent", width: 230 },
];

export const ghostDeclinedColumns: ColDef<GhostDeclinedRecord>[] = [
  { field: "reference_id", headerName: "Reference ID", width: 200 },
  { field: "ronin_order_id", headerName: "Ronin Order", width: 120 },
  { field: "location_name", headerName: "Location", width: 160 },
  { field: "ronin_status", headerName: "Order Status", width: 110 },
  { field: "payment_status", headerName: "Pay Status", width: 110 },
  { field: "transaction_status", headerName: "Txn Status", width: 110 },
  { field: "ronin_amount_cents", headerName: "Ronin Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_amount_cents", headerName: "Proc. Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_status", headerName: "Proc. Status", width: 200 },
  { field: "mx_date", headerName: "Proc. Date", width: 160, valueFormatter: dateFormatter },
  { field: "mx_last4", headerName: "Proc. Last 4", width: 100 },
  { field: "mx_response_message", headerName: "Response", width: 180 },
  { field: "ronin_transaction_at", headerName: "Ronin Date", width: 160, valueFormatter: dateFormatter },
  { field: "payment_intent_id", headerName: "Stripe PaymentIntent", width: 230 },
];

export const ghostApprovedColumns: ColDef<GhostApprovedRecord>[] = [
  { field: "reference_id", headerName: "Reference ID", width: 200 },
  { field: "ronin_order_id", headerName: "Ronin Order", width: 120 },
  { field: "location_name", headerName: "Location", width: 160 },
  { field: "ronin_status", headerName: "Order Status", width: 110 },
  { field: "payment_status", headerName: "Pay Status", width: 110 },
  { field: "transaction_status", headerName: "Txn Status", width: 110 },
  { field: "ronin_amount_cents", headerName: "Ronin Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_amount_cents", headerName: "Proc. Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_status", headerName: "Proc. Status", width: 200 },
  { field: "mx_date", headerName: "Proc. Date", width: 160, valueFormatter: dateFormatter },
  { field: "mx_last4", headerName: "Proc. Last 4", width: 100 },
  { field: "mx_card_type", headerName: "Proc. Card Type", width: 120 },
  { field: "ronin_transaction_at", headerName: "Ronin Date", width: 160, valueFormatter: dateFormatter },
  { field: "payment_intent_id", headerName: "Stripe PaymentIntent", width: 230 },
];

export const orphanMXColumns: ColDef<OrphanMXRecord>[] = [
  { field: "reference_id", headerName: "Reference ID", width: 200 },
  { field: "payment_intent_id", headerName: "Stripe PaymentIntent", width: 230 },
  { field: "mx_amount_cents", headerName: "Proc. Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_status", headerName: "Proc. Status", width: 140 },
  { field: "mx_type", headerName: "Proc. Type", width: 120 },
  { field: "mx_date", headerName: "Proc. Date", width: 160, valueFormatter: dateFormatter },
  { field: "mx_last4", headerName: "Last 4", width: 100 },
  { field: "mx_card_type", headerName: "Card Type", width: 120 },
  { field: "invoice", headerName: "Invoice", width: 180 },
  { field: "memo", headerName: "Memo", width: 300 },
];

export const orphanRoninColumns: ColDef<OrphanRoninRecord>[] = [
  { field: "reference_id", headerName: "Reference ID", width: 200 },
  { field: "payment_intent_id", headerName: "Stripe PaymentIntent", width: 230 },
  { field: "ronin_order_id", headerName: "Ronin Order", width: 120 },
  { field: "location_name", headerName: "Location", width: 160 },
  { field: "ronin_status", headerName: "Order Status", width: 110 },
  { field: "payment_status", headerName: "Pay Status", width: 110 },
  { field: "transaction_status", headerName: "Txn Status", width: 110 },
  { field: "ronin_amount_cents", headerName: "Ronin Amount", width: 130, valueFormatter: centsFormatter },
  { field: "ronin_transaction_at", headerName: "Ronin Date", width: 160, valueFormatter: dateFormatter },
  { field: "ronin_last4", headerName: "Last 4", width: 100 },
  { field: "ronin_card_type", headerName: "Card Type", width: 120 },
];

// Pairs matched by amount + last4 when the invoice reference_id was
// unrecoverable. Surfaces both reference_ids side-by-side so the user can see
// where the IDs diverged.
export const idMismatchedColumns: ColDef<IdMismatchedRecord>[] = [
  { field: "ronin_reference_id", headerName: "Ronin Ref ID", width: 200 },
  { field: "mx_reference_id", headerName: "Proc. Ref ID", width: 200 },
  { field: "matched_by", headerName: "Matched By", width: 140, valueFormatter: (p) => p.value || "Amount + Last 4" },
  { field: "payment_intent_id", headerName: "Stripe PaymentIntent", width: 230 },
  { field: "ronin_order_id", headerName: "Ronin Order", width: 120 },
  { field: "location_name", headerName: "Location", width: 160 },
  { field: "ronin_amount_cents", headerName: "Ronin Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_amount_cents", headerName: "Proc. Amount", width: 130, valueFormatter: centsFormatter },
  { field: "ronin_last4", headerName: "Ronin Last 4", width: 110 },
  { field: "mx_last4", headerName: "Proc. Last 4", width: 110 },
  { field: "mx_status", headerName: "Proc. Status", width: 180 },
  { field: "mx_date", headerName: "Proc. Date", width: 150, valueFormatter: dateFormatter },
  { field: "ronin_transaction_at", headerName: "Ronin Date", width: 150, valueFormatter: dateFormatter },
  { field: "mx_card_type", headerName: "Proc. Card", width: 110 },
  { field: "mx_invoice", headerName: "Proc. Invoice (raw)", width: 160 },
];

// Duplicate Ronin orders: two orders for one Stripe charge (same PaymentIntent).
// Void the dup (ronin_order_id); keep the order that owns the charge (keeper_order_id).
export const duplicateOrdersColumns: ColDef<IdMismatchedRecord>[] = [
  {
    field: "ronin_order_id", headerName: "Void (dup order)", width: 150,
    cellStyle: { color: "#dc2626", fontWeight: "bold" },
  },
  { field: "ronin_reference_id", headerName: "Dup Ref", width: 190 },
  {
    field: "keeper_order_id", headerName: "Keep (order)", width: 130,
    cellStyle: { color: "#16a34a", fontWeight: "bold" },
  },
  { field: "mx_reference_id", headerName: "Keep Ref", width: 190 },
  { field: "payment_intent_id", headerName: "Stripe PaymentIntent", width: 230 },
  { field: "ronin_amount_cents", headerName: "Amount", width: 110, valueFormatter: centsFormatter },
  { field: "location_name", headerName: "Location", width: 160 },
  { field: "ronin_transaction_at", headerName: "Ronin Date", width: 160, valueFormatter: dateFormatter },
];

// Double charges: one reference charged 2+ times in Stripe. Keep one charge,
// refund the rest (overcharge column = the amount to give back to the customer).
export const doubleChargeColumns: ColDef<DoubleChargeRecord>[] = [
  { field: "reference_id", headerName: "Reference ID", width: 190 },
  { field: "ronin_order_id", headerName: "Ronin Order", width: 120 },
  { field: "location_name", headerName: "Location", width: 160 },
  {
    field: "times_charged", headerName: "Times Charged", width: 120,
    cellStyle: { color: "#dc2626", fontWeight: "bold", textAlign: "center" },
  },
  { field: "total_charged_cents", headerName: "Total Charged", width: 130, valueFormatter: centsFormatter },
  { field: "should_be_cents", headerName: "Should Be", width: 120, valueFormatter: centsFormatter },
  {
    field: "overcharge_cents", headerName: "Refund (overcharge)", width: 160, valueFormatter: centsFormatter,
    cellStyle: { color: "#dc2626", fontWeight: "bold" },
  },
  { field: "refund_payment_intents", headerName: "Refund These PaymentIntents", width: 280 },
  { field: "keep_payment_intent", headerName: "Keep", width: 230 },
  { field: "mx_date", headerName: "Date", width: 160, valueFormatter: dateFormatter },
];

export const voidedColumns: ColDef<VoidedOrderRecord>[] = [
  {
    field: "mx_still_charged", headerName: "Status", width: 140,
    cellStyle: (p): CellStyle | undefined => {
      if (p.value) return { color: "#dc2626", fontWeight: "bold" };
      return { color: "#16a34a" };
    },
    valueFormatter: (p) => (p.value ? "STILL CHARGED" : "Both Agree"),
    sort: "desc",
  },
  { field: "reference_id", headerName: "Reference ID", width: 200 },
  { field: "ronin_order_id", headerName: "Ronin Order", width: 120 },
  { field: "location_name", headerName: "Location", width: 160 },
  { field: "ronin_status", headerName: "Order Status", width: 110 },
  { field: "payment_status", headerName: "Pay Status", width: 110 },
  { field: "transaction_status", headerName: "Txn Status", width: 110 },
  { field: "ronin_amount_cents", headerName: "Ronin Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_status", headerName: "Proc. Status", width: 200 },
  { field: "mx_amount_cents", headerName: "Proc. Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_date", headerName: "Proc. Date", width: 160, valueFormatter: dateFormatter },
  { field: "ronin_transaction_at", headerName: "Ronin Date", width: 160, valueFormatter: dateFormatter },
  { field: "payment_intent_id", headerName: "Stripe PaymentIntent", width: 230 },
];

/* ------------------------------------------------------------------ */
/*  Status Mismatch detection                                         */
/* ------------------------------------------------------------------ */

const SUCCESS_STATUSES = new Set(["approved", "settled", "captured", "completed", "complete", "paid"]);
const FAILURE_STATUSES = new Set(["declined", "failed", "error", "voided", "cancelled", "canceled", "refunded", "void"]);
const PENDING_STATUSES = new Set(["pending", "processing", "created", "open"]);

type StatusBucket = "success" | "failure" | "pending" | "unknown";

function toBucket(status: string | null | undefined): StatusBucket {
  if (!status) return "unknown";
  const s = status.trim().toLowerCase();
  if (SUCCESS_STATUSES.has(s)) return "success";
  if (FAILURE_STATUSES.has(s)) return "failure";
  if (PENDING_STATUSES.has(s)) return "pending";
  return "unknown";
}

const BUCKET_LABELS: Record<StatusBucket, string> = {
  success: "✓",
  failure: "✗",
  pending: "…",
  unknown: "?",
};

/**
 * Scan all reconciliation categories for records where the three Ronin-side
 * statuses (order, payment, transaction) fall into different buckets.
 */
export function findStatusMismatches(data: ReconciliationResponse): StatusMismatchRecord[] {
  const results: StatusMismatchRecord[] = [];

  function check(
    refId: string, orderId: number, location: string, amount: number, date: string,
    orderStatus: string, payStatus: string | null, txnStatus: string | null,
    mxStatus: string | null, mxAmount: number | null, sourceTab: string,
  ) {
    const ob = toBucket(orderStatus);
    const pb = toBucket(payStatus);
    const tb = toBucket(txnStatus);

    // Only flag when at least two non-unknown buckets disagree
    const known = [ob, pb, tb].filter(b => b !== "unknown");
    if (known.length < 2) return;                    // not enough data to compare
    if (new Set(known).size === 1) return;            // all agree

    results.push({
      reference_id: refId, ronin_order_id: orderId, location_name: location,
      ronin_amount_cents: amount, ronin_transaction_at: date,
      order_status: orderStatus, payment_status: payStatus, transaction_status: txnStatus,
      order_bucket: ob, payment_bucket: pb, transaction_bucket: tb,
      mx_status: mxStatus, mx_amount_cents: mxAmount, source_tab: sourceTab,
    });
  }

  for (const r of data.matched) {
    check(r.reference_id, r.ronin_order_id, r.location_name, r.ronin_amount_cents,
      r.ronin_transaction_at, r.ronin_status, r.payment_status, r.transaction_status,
      r.mx_status, r.mx_amount_cents, "Matched");
  }
  for (const r of data.ghostDeclined) {
    check(r.reference_id, r.ronin_order_id, r.location_name, r.ronin_amount_cents,
      r.ronin_transaction_at, r.ronin_status, r.payment_status, r.transaction_status,
      r.mx_status, r.mx_amount_cents, "Ghost Declined");
  }
  for (const r of data.ghostApproved) {
    check(r.reference_id, r.ronin_order_id, r.location_name, r.ronin_amount_cents,
      r.ronin_transaction_at, r.ronin_status, r.payment_status, r.transaction_status,
      r.mx_status, r.mx_amount_cents, "Ghost Approved");
  }
  for (const r of data.orphanRonin) {
    check(r.reference_id, r.ronin_order_id, r.location_name, r.ronin_amount_cents,
      r.ronin_transaction_at, r.ronin_status, r.payment_status, r.transaction_status,
      null, null, "Orphan Ronin");
  }
  for (const r of data.voidedOrders) {
    check(r.reference_id, r.ronin_order_id, r.location_name, r.ronin_amount_cents,
      r.ronin_transaction_at, r.ronin_status, r.payment_status, r.transaction_status,
      r.mx_status ?? null, r.mx_amount_cents ?? null, "Voided");
  }

  return results;
}

const bucketCellStyle = (p: { value: StatusBucket }): CellStyle | undefined => {
  switch (p.value) {
    case "success": return { backgroundColor: "#f0fdf4", color: "#16a34a", fontWeight: "bold", textAlign: "center" };
    case "failure": return { backgroundColor: "#fef2f2", color: "#dc2626", fontWeight: "bold", textAlign: "center" };
    case "pending": return { backgroundColor: "#fefce8", color: "#ca8a04", fontWeight: "bold", textAlign: "center" };
    default:        return { color: "#9ca3af", textAlign: "center" };
  }
};

export const statusMismatchColumns: (ColDef<StatusMismatchRecord> | ColGroupDef<StatusMismatchRecord>)[] = [
  { field: "source_tab", headerName: "Found In", width: 140, pinned: "left" },
  { field: "reference_id", headerName: "Reference ID", width: 200 },
  { field: "ronin_order_id", headerName: "Ronin Order", width: 120 },
  { field: "location_name", headerName: "Location", width: 160 },
  {
    headerName: "Order Status", width: 130,
    children: [
      { field: "order_status", headerName: "Value", width: 120 },
      { field: "order_bucket", headerName: "", width: 50, cellStyle: bucketCellStyle, valueFormatter: (p) => BUCKET_LABELS[p.value as StatusBucket] || "?" },
    ],
  },
  {
    headerName: "Payment Status", width: 130,
    children: [
      { field: "payment_status", headerName: "Value", width: 120, valueFormatter: (p) => p.value || "—" },
      { field: "payment_bucket", headerName: "", width: 50, cellStyle: bucketCellStyle, valueFormatter: (p) => BUCKET_LABELS[p.value as StatusBucket] || "?" },
    ],
  },
  {
    headerName: "Transaction Status", width: 130,
    children: [
      { field: "transaction_status", headerName: "Value", width: 120, valueFormatter: (p) => p.value || "—" },
      { field: "transaction_bucket", headerName: "", width: 50, cellStyle: bucketCellStyle, valueFormatter: (p) => BUCKET_LABELS[p.value as StatusBucket] || "?" },
    ],
  },
  { field: "mx_status", headerName: "Proc. Status", width: 180, valueFormatter: (p) => p.value || "—" },
  { field: "ronin_amount_cents", headerName: "Ronin Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_amount_cents", headerName: "Proc. Amount", width: 130, valueFormatter: (p) => formatCents(p.value) || "—" },
  { field: "ronin_transaction_at", headerName: "Ronin Date", width: 160, valueFormatter: dateFormatter },
];

export const unmatchedRefColumns: ColDef<UnmatchedRefRecord>[] = [
  { field: "reference_id", headerName: "Reference ID", width: 220 },
  { field: "mx_rows", headerName: "Proc. Rows", width: 100 },
  { field: "mx_total_cents", headerName: "Proc. Total", width: 130, valueFormatter: centsFormatter },
  {
    field: "statuses", headerName: "Statuses", width: 220,
    valueFormatter: (p) => {
      if (!p.value) return "";
      return Object.entries(p.value as Record<string, number>)
        .map(([s, c]) => `${s} (${c})`)
        .join(", ");
    },
  },
  { field: "mx_date_earliest", headerName: "Earliest Date", width: 160, valueFormatter: dateFormatter },
  { field: "mx_date_latest", headerName: "Latest Date", width: 160, valueFormatter: dateFormatter },
];

export const duplicateMXColumns: ColDef<DuplicateMXRecord>[] = [
  { field: "reference_id", headerName: "Reference ID", width: 200 },
  { field: "mx_amount_cents", headerName: "Proc. Amount", width: 130, valueFormatter: centsFormatter },
  { field: "mx_status", headerName: "Duplicate Status", width: 140 },
  { field: "mx_date", headerName: "Duplicate Date", width: 160, valueFormatter: dateFormatter },
  { field: "mx_type", headerName: "Proc. Type", width: 120 },
  { field: "mx_last4", headerName: "Last 4", width: 100 },
  { field: "mx_card_type", headerName: "Card Type", width: 120 },
  { field: "invoice", headerName: "Invoice", width: 180 },
  { field: "original_mx_date", headerName: "Original Date", width: 160, valueFormatter: dateFormatter },
];
